import type { CallIncomingPayload } from "~/services/calling/types";
import { asEventNumber, asEventObject, asEventString } from "~/context/call/helpers";

function asEventBoolean(value: unknown): boolean | undefined {
  if (typeof value === "boolean") return value;
  if (typeof value === "number" && Number.isFinite(value)) {
    if (value === 1) return true;
    if (value === 0) return false;
    return undefined;
  }
  if (typeof value !== "string") return undefined;
  const normalized = value.trim().toLowerCase();
  if (["true", "yes", "y", "1", "on"].includes(normalized)) return true;
  if (["false", "no", "n", "0", "off"].includes(normalized)) return false;
  return undefined;
}

function parseEventJsonObject(value: unknown): Record<string, unknown> | null {
  if (typeof value !== "string") return null;
  const raw = value.trim();
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    return asEventObject(parsed);
  } catch {
    return null;
  }
}

/**
 * Builds an alarm payload from any realtime event that looks like one (alarm, signal, scaip),
 * including the sidecar's raw `scaip:message`. Kept out of useCallSocket so it can be tested on
 * its own.
 */
export function inferAlarmPayloadFromAnyEvent(
  payload: unknown,
  normalizedEventName: string
): CallIncomingPayload | null {
  const objectPayload = asEventObject(payload);
  if (!objectPayload) return null;

  const queryPayload = asEventObject(objectPayload.query);
  const bodyPayload = asEventObject(objectPayload.body);
  const metadataPayload = asEventObject(objectPayload.metadata);
  const rawBodyText =
    asEventString(objectPayload.scaipBody) ??
    asEventString(objectPayload.body) ??
    asEventString(queryPayload?.body);
  const parsedBodyPayload = parseEventJsonObject(rawBodyText);

  const candidateObjects = [
    objectPayload,
    queryPayload,
    bodyPayload,
    metadataPayload,
    parsedBodyPayload,
  ].filter((candidate): candidate is Record<string, unknown> => Boolean(candidate));

  const readString = (...keys: string[]) => {
    for (const source of candidateObjects) {
      for (const key of keys) {
        const value = asEventString(source[key]);
        if (value) return value;
      }
    }
    return undefined;
  };

  const readBoolean = (...keys: string[]) => {
    for (const source of candidateObjects) {
      for (const key of keys) {
        const value = asEventBoolean(source[key]);
        if (typeof value === "boolean") return value;
      }
    }
    return undefined;
  };

  const readNumber = (...keys: string[]) => {
    for (const source of candidateObjects) {
      for (const key of keys) {
        const value = asEventNumber(source[key]);
        if (typeof value === "number" && Number.isFinite(value)) return value;
      }
    }
    return undefined;
  };

  const callUuid = readString(
    "callUuid",
    "call_uuid",
    "uniqueId",
    "unique_id",
    "id"
  );
  const id = readString("id") ?? callUuid;
  const callerNumber = readString(
    "callerNumber",
    "caller_number",
    "caller",
    "from",
    "phoneNumber",
    "phone_number",
    "ani",
    "msisdn"
  );
  const customerName = readString("customerName", "customer_name", "name");
  const customerId = readString("customerId", "customer_id", "clientId");
  // The sidecar's scaip:message names the unit as `cid` (what the device sent); without it a
  // live alarm row has no unit and cannot be matched to its client (AC-400).
  const deviceCid = readString("deviceCid", "device_cid", "cid");
  const service = readString(
    "service",
    "deviceType",
    "device_type",
    "queue",
    "source"
  );
  const explicitSignalType = readString(
    "signalType",
    "signal_type",
    "eventType",
    "event_type",
    "type"
  );
  const explicitDescription = readString(
    "description",
    "reason",
    "message",
    "event"
  );
  const signalType = explicitSignalType ?? normalizedEventName;
  const description = explicitDescription ?? rawBodyText ?? signalType;
  const requiresAnswerExplicit = readBoolean(
    "requiresAnswer",
    "requires_answer",
    "answerRequired",
    "answer_required",
    "needsAnswer",
    "needs_answer"
  );
  const noAnswerRequired = readBoolean(
    "noAnswerRequired",
    "no_answer_required",
    "isInformational",
    "is_informational",
    "informational",
    "infoOnly",
    "info_only"
  );
  const requiresAnswer =
    typeof requiresAnswerExplicit === "boolean"
      ? requiresAnswerExplicit
      : typeof noAnswerRequired === "boolean"
        ? !noAnswerRequired
        : undefined;
  // scaip:message sends `timestamp` as an ISO string, which readNumber cannot read.
  const timestampText = readString("timestamp", "createdAt", "created_at");
  const parsedTimestampMs = timestampText ? Date.parse(timestampText) : Number.NaN;
  const receivedAtMs =
    readNumber(
      "receivedAtMs",
      "received_at_ms",
      "timestamp",
      "createdAt",
      "created_at"
    ) ?? (Number.isFinite(parsedTimestampMs) ? parsedTimestampMs : Date.now());

  const hasContent =
    Boolean(id) ||
    Boolean(callUuid) ||
    Boolean(callerNumber) ||
    Boolean(customerName) ||
    Boolean(service) ||
    Boolean(rawBodyText) ||
    Boolean(explicitDescription) ||
    Boolean(explicitSignalType) ||
    typeof requiresAnswer === "boolean";

  if (!hasContent) return null;

  const metadata = parsedBodyPayload
    ? {
        ...objectPayload,
        parsedBody: parsedBodyPayload,
      }
    : objectPayload;

  return {
    id: id ?? undefined,
    callUuid: callUuid ?? undefined,
    callerNumber: callerNumber ?? undefined,
    customerName: customerName ?? undefined,
    customerId: customerId ?? undefined,
    deviceCid: deviceCid ?? undefined,
    service: service ?? undefined,
    signalType: signalType ?? undefined,
    description: description ?? undefined,
    requiresAnswer: requiresAnswer ?? undefined,
    metadata,
    receivedAtMs,
  };
}
